import React, { cloneElement, forwardRef, useCallback, useRef, useState } from "react";
import { Manager, Popper, Reference } from "react-popper";
import classNames from "classnames";
import { useMatch, useNavigate } from "react-router-dom";
import themeConfig from "../../config/theme.config";
import Icon from "../icon/Icon";
import useOnClickOutside from "../../hooks/useOnClickOutside";

const Dropdown = (props) => {
  const { children, className, isOpen = null, setIsOpen, tag: Tag = "div" } = props;

  const [state, setState] = useState(!!(isOpen !== null && !!setIsOpen ? isOpen : false));

  const dropdownRef = useRef(null);

  const classes = classNames("inline-flex");

  const closeMenu = useCallback(() => {
    if (isOpen !== null && !!setIsOpen) {
      setIsOpen(false);
    } else {
      setState(false);
    }
  }, [isOpen, setIsOpen]);
  useOnClickOutside(dropdownRef, closeMenu);

  return (
    <Manager>
      <Tag data-component-name="Dropdown" ref={dropdownRef} className={classNames(classes, className)}>
        {children?.map((child, index) =>
          ["DropdownMenu", "DropdownToggle"].includes(child.type.displayName)
            ? cloneElement(child, {
                isOpen: isOpen !== null && !!setIsOpen ? isOpen : state,
                setIsOpen: isOpen !== null && !!setIsOpen ? setIsOpen : setState,

                key: index,
              })
            : child
        )}
      </Tag>
    </Manager>
  );
};
Dropdown.displayName = "Dropdown";

export const DropdownToggle = (props) => {
  const { children, isOpen = false, setIsOpen, hasIcon = true } = props;

  const dropdownButtonRef = useRef(null);
  const setButtonRef = useCallback((node, ref) => {
    dropdownButtonRef.current = node;

    return ref(node);
  }, []);

  return (
    <Reference>
      {({ ref }) =>
        cloneElement(children, {
          "data-component-name": `Dropdown/DropdownToggle [${children.type.displayName}]`,
          ref: (node) => setButtonRef(node, ref),
          onClick: () => {
            children?.props?.onClick?.();

            if (setIsOpen) {
              setIsOpen(!isOpen);
            }
          },
          rightIcon: hasIcon
            ? hasIcon(children.type.displayName === "Button" && "HeroChevronDown") || "HeroChevronRight"
            : null,
          isActive: isOpen,
          className: classNames(
            {
              show: isOpen,
            },
            children?.props?.className
          ),
          "aria-expanded": isOpen,
        })
      }
    </Reference>
  );
};
DropdownToggle.displayName = "DropdownToggle";

export const DropdownMenu = (props) => {
  const {
    isOpen = false,
    setIsOpen,
    children,
    className,
    placement = "bottom-start",
    isCloseAfterLeave = true,
    borderWidth = themeConfig.borderWidth,
    rounded = themeConfig.rounded,
    fallbackPlacements = [`top-start`, `bottom-start`],
    ...rest
  } = props;

  const dropdownListRef = useRef(null);

  const setListRef = useCallback((node, ref) => {
    dropdownListRef.current = node;

    return ref(node);
  }, []);

  const modifiers = [
    {
      name: "flip",
      options: {
        fallbackPlacements,
      },
    },
  ];

  const onMouseLeave = isCloseAfterLeave && setIsOpen ? () => setIsOpen(false) : undefined;

  if (isOpen) {
    return (
      <Popper placement={placement} modifiers={modifiers}>
        {({ ref, style }) => (
          <ul
            data-component-name="Dropdown/DropdownMenu"
            role="presentation"
            ref={(node) => setListRef(node, ref)}
            style={style}
            data-placement={placement}
            className={classNames(
              classNames(
                "py-2",
                "z-[9999]",
                "border-zinc-300/25 bg-white shadow-lg dark:border-zinc-800/50 dark:bg-zinc-900",
                [`${borderWidth}`, `${rounded}`]
              ),
              className
            )}
            onMouseLeave={onMouseLeave}
            {...rest}
          >
            {children}
          </ul>
        )}
      </Popper>
    );
  }
  return null;
};
DropdownMenu.displayName = "DropdownMenu";

export const DropdownItem = forwardRef((props, ref) => {
  const {
    children,
    className,
    color = themeConfig.themeColor,
    colorIntensity = themeConfig.themeColorShade,
    isActive = false,
    icon,
    rightIcon,
    ...rest
  } = props;
  const classes = classNames(
    "px-4 py-2",
    "flex items-center",
    "whitespace-nowrap",
    "cursor-pointer",
    "border-zinc-300/25 dark:border-zinc-800/50",
    {
      [`text-${color}-${colorIntensity}`]: isActive,
      "text-zinc-500 hover:text-zinc-950 dark:hover:text-zinc-100": !isActive,
    },
    themeConfig.transition
  );
  return (
    <li data-component-name="Dropdown/DropdownItem" ref={ref} className={classNames(classes, className)} {...rest}>
      {icon && <Icon icon={icon} className="inline-flex text-xl ltr:mr-1.5 rtl:ml-1.5" />}
      {children}
      {rightIcon && <Icon icon={rightIcon} className="inline-flex text-xl ltr:ml-1.5 rtl:mr-1.5" />}
    </li>
  );
});
DropdownItem.displayName = "DropdownItem";

export const DropdownNavLinkItem = (props) => {
  const { to, children, ...rest } = props;

  const navigate = useNavigate();
  const match = useMatch({ path: to });

  return (
    <DropdownItem {...rest} onClick={() => navigate(to)} isActive={!!match}>
      {children}
    </DropdownItem>
  );
};

export default Dropdown;
